import {
  ComponentType,
  LazyExoticComponent,
  PropsWithoutRef,
  ReactElement,
  Suspense,
  SuspenseProps,
  lazy,
} from 'react';
import ErrorBoundary, { ErrorBoundaryProps } from '../ErrorBoundary';
import { Loader } from './Loader';
import { lazyRetry } from './Loadable.utils';

export interface LoadableProps<Props extends object>
  extends ErrorBoundaryProps {
  Component: LazyExoticComponent<ComponentType<Props>>;
  componentProps?: Props;
  loader?: SuspenseProps['fallback'];
}

function Loadable<Props extends object>({
  Component,
  componentProps,
  loader,
  ...errorBoundaryProps
}: LoadableProps<Props>) {
  return (
    <Suspense fallback={loader || <Loader />}>
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...(componentProps as PropsWithoutRef<Props>)} />
      </ErrorBoundary>
    </Suspense>
  );
}

type Loader<Props extends object> = () => Promise<{
  default: ComponentType<Props>;
}>;

const elementCache = new WeakMap<Loader<object>, ReactElement>();

export const loadable = <Props extends object>(
  load: Loader<Props>
): ReactElement => {
  const key = load as unknown as Loader<object>;
  const cached = elementCache.get(key);

  if (cached) {
    return cached;
  }

  const Component = lazy(() => lazyRetry(load));
  const element = <Loadable Component={Component} />;
  elementCache.set(key, element);

  return element;
};

export default Loadable;
